import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { usePageTracking } from './hooks/useAnalytics';
import WelcomePage      from './pages/WelcomePage';
import LoginPage        from './pages/LoginPage';
import RegisterPage     from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HomePage         from './pages/HomePage';
import FindSpotPage     from './pages/FindSpotPage';
import SessionPage      from './pages/SessionPage';
import MySpotsPage      from './pages/MySpotsPage';
import RegisterSpotPage from './pages/RegisterSpotPage';
import SpotDetailPage   from './pages/SpotDetailPage';
import MarkFreePage     from './pages/MarkFreePage';
import SchedulePage     from './pages/SchedulePage';
import EditAttributesPage from './pages/EditAttributesPage';
import RecentClaimsPage from './pages/RecentClaimsPage';
import AdminPage        from './pages/AdminPage';
import AdminLevelsPage  from './pages/AdminLevelsPage';
import ProfilePage      from './pages/ProfilePage';
import FeedbackPage     from './pages/FeedbackPage';
import { SpotRequestPage } from './pages/SpotRequestPage';
import NotFoundPage     from './pages/NotFoundPage';
import MaintenancePage  from './pages/MaintenancePage';
import JoinBuildingPage from './pages/JoinBuildingPage';
import LevelLadderPage  from './pages/LevelLadderPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const maintenanceMode = useAuthStore((s) => s.maintenanceMode);
  const user = useAuthStore((s) => s.user);
  if (!accessToken) {
    const seen = localStorage.getItem('onboarding_seen');
    return <Navigate to={seen ? '/login' : '/welcome'} replace />;
  }
  if (maintenanceMode && !user?.super_admin) return <Navigate to="/maintenance" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  if (accessToken) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RouteLayout() {
  usePageTracking();
  return <Routes>
        {/* Welcome onboarding — shown once to new visitors */}
        <Route path="/welcome"  element={<PublicRoute><WelcomePage /></PublicRoute>} />

        {/* Public — redirect to / when already logged in */}
        <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
        <Route path="/join" element={<PublicRoute><JoinBuildingPage /></PublicRoute>} />

        {/* Maintenance page — accessible without authentication */}
        <Route path="/maintenance" element={<MaintenancePage />} />

        {/* Protected */}
        <Route path="/"                   element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/find"               element={<ProtectedRoute><FindSpotPage /></ProtectedRoute>} />
        <Route path="/requests"           element={<ProtectedRoute><SpotRequestPage /></ProtectedRoute>} />
        <Route path="/session"            element={<ProtectedRoute><SessionPage /></ProtectedRoute>} />
        <Route path="/spots"              element={<ProtectedRoute><MySpotsPage /></ProtectedRoute>} />
        <Route path="/spots/new"          element={<ProtectedRoute><RegisterSpotPage /></ProtectedRoute>} />
        <Route path="/spots/:id"          element={<ProtectedRoute><SpotDetailPage /></ProtectedRoute>} />
        <Route path="/spots/:id/free"     element={<ProtectedRoute><MarkFreePage /></ProtectedRoute>} />
        <Route path="/spots/:id/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
        <Route path="/spots/:id/attributes" element={<ProtectedRoute><EditAttributesPage /></ProtectedRoute>} />
        <Route path="/spots/recent-claims"  element={<ProtectedRoute><RecentClaimsPage /></ProtectedRoute>} />
        <Route path="/profile"            element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/feedback"           element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
        <Route path="/levels"             element={<ProtectedRoute><LevelLadderPage /></ProtectedRoute>} />

        {/* Admin only */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/levels"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLevelsPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>;
  }

export function Router() {
  return (
    <BrowserRouter>
      <RouteLayout />
    </BrowserRouter>
  );
}
